import { useLazyQuery } from "@apollo/client";
import { useCallback, useRef, useState } from "react";

import { showErrorSnackbar } from "@/lib/snackbarUtils";

import { CAREER_PLAN_DOWNLOAD_ERROR } from "./careerPlanStatusActions";
import { isLocalObjectStoragePdfUrl, openCareerPlanPdf } from "./_openCareerPlanPdf";
import { GET_PERSON_CAREER_PLAN_PDF_QUERY } from "./queries";

function openHostedPdfPreview(): Window | null {
  if (typeof window === "undefined" || typeof window.open !== "function") {
    return null;
  }
  return window.open("about:blank", "_blank");
}

export function useDownloadCareerPlanPdf(pgaId: string) {
  const [fetchPdf] = useLazyQuery(GET_PERSON_CAREER_PLAN_PDF_QUERY, {
    fetchPolicy: "no-cache",
    errorPolicy: "all"
  });
  const [downloadingPlanId, setDownloadingPlanId] = useState<string | null>(null);
  const downloadingRef = useRef(false);

  const downloadPdf = useCallback(
    async (planId: string) => {
      if (!planId || downloadingRef.current) return;

      downloadingRef.current = true;
      setDownloadingPlanId(planId);
      const preview = openHostedPdfPreview();
      if (!preview) {
        downloadingRef.current = false;
        setDownloadingPlanId(null);
        showErrorSnackbar(CAREER_PLAN_DOWNLOAD_ERROR);
        return;
      }
      try {
        const result = await fetchPdf({ variables: { pgaId, id: planId } });
        const pdfUrl = result.data?.person?.careerPlan?.pdfUrl;
        if (!pdfUrl) {
          preview.close();
          showErrorSnackbar(CAREER_PLAN_DOWNLOAD_ERROR);
          return;
        }
        if (isLocalObjectStoragePdfUrl(pdfUrl)) {
          preview.close();
          await openCareerPlanPdf(pdfUrl);
          return;
        }
        preview.location.replace(pdfUrl);
      } catch {
        preview.close();
        showErrorSnackbar(CAREER_PLAN_DOWNLOAD_ERROR);
      } finally {
        downloadingRef.current = false;
        setDownloadingPlanId(null);
      }
    },
    [fetchPdf, pgaId]
  );

  return { downloadPdf, downloadingPlanId };
}
